import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  Save, 
  Loader, 
  Layout,
  Palette,
  Settings,
  Eye,
  Timer,
  MousePointer,
  Sparkles,
  Zap,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AdminBanner } from '../../services/contentService';

interface EditBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (bannerId: string, bannerData: Partial<AdminBanner>) => Promise<void>;
  banner: AdminBanner | null;
}

export function EditBannerModal({ isOpen, onClose, onSubmit, banner }: EditBannerModalProps) {
  const [formData, setFormData] = useState({
    text: '',
    backgroundColor: 'jamaica-green' as 'jamaica-green' | 'jamaica-black',
    isActive: false,
    order: 1,
    link: '',
    showCloseButton: false,
    countdown: {
      enabled: false,
      endDate: '',
      format: 'full' as 'days' | 'hours' | 'minutes' | 'full',
      prefix: 'Ends in:',
      suffix: 'remaining'
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form data when banner changes
  useEffect(() => {
    if (banner) {
      // Convert countdown endDate if it's a Timestamp
      let endDate = '';
      if (banner.countdown?.endDate) {
        if (typeof banner.countdown.endDate === 'string') {
          endDate = banner.countdown.endDate;
        } else if (banner.countdown.endDate instanceof Date) {
          endDate = banner.countdown.endDate.toISOString().slice(0, 16);
        } else if (banner.countdown.endDate.toDate) {
          // Firebase Timestamp
          endDate = banner.countdown.endDate.toDate().toISOString().slice(0, 16);
        }
      }

      setFormData({
        text: banner.text || '',
        backgroundColor: banner.backgroundColor || 'jamaica-green',
        isActive: banner.isActive || false,
        order: banner.order || 1,
        link: banner.link || '',
        showCloseButton: banner.showCloseButton || false,
        countdown: {
          enabled: !!banner.countdown?.enabled,
          endDate: endDate,
          format: banner.countdown?.format || 'full',
          prefix: banner.countdown?.prefix || 'Ends in:',
          suffix: banner.countdown?.suffix || 'remaining'
        }
      });
    }
  }, [banner]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.text.trim()) {
      newErrors.text = 'Banner text is required';
    }

    if (formData.countdown.enabled && !formData.countdown.endDate) {
      newErrors.countdownEndDate = 'End date is required when countdown is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !banner) return;

    setIsSubmitting(true);
    try {
      const bannerData: Partial<AdminBanner> = {
        text: formData.text.trim(),
        backgroundColor: formData.backgroundColor,
        isActive: formData.isActive,
        order: formData.order,
        link: formData.link?.trim() ? formData.link.trim() : undefined,
        showCloseButton: formData.showCloseButton,
        countdown: {
          ...formData.countdown,
          endDate: formData.countdown.endDate
        }
      };

      await onSubmit(banner.id, bannerData);
      toast.success('Banner updated successfully');
      handleClose();
    } catch (error) {
      console.error('Error updating banner:', error);
      toast.error('Failed to update banner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      text: '',
      backgroundColor: 'jamaica-green',
      isActive: false,
      order: 1,
      link: '',
      showCloseButton: false,
      countdown: {
        enabled: false,
        endDate: '',
        format: 'full',
        prefix: 'Ends in:',
        suffix: 'remaining'
      }
    });
    setErrors({});
    onClose();
  };

  if (!isOpen || !banner) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Layout className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Edit Banner
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Update banner {banner.order} settings
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 space-y-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            {/* Banner Text */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-3"
            >
              <Label htmlFor="text" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-jamaica-green" />
                Banner Message *
              </Label>
              <Textarea
                id="text"
                placeholder="Update your engaging banner message..."
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                className={`transition-all duration-200 resize-none ${
                  errors.text 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20'
                } rounded-xl text-base p-4 min-h-[100px] shadow-sm hover:shadow-md focus:shadow-lg`}
                rows={3}
              />
              {errors.text && (
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-sm text-red-600 flex items-center gap-1"
                >
                  <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                  {errors.text}
                </motion.p>
              )}
            </motion.div>

            {/* Theme Selection */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-3"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Palette className="h-4 w-4 text-jamaica-green" />
                Theme & Style
              </Label>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: 'jamaica-green', label: 'Jamaica Green', bg: 'bg-jamaica-green', preview: 'from-jamaica-green to-jamaica-green/80' },
                  { value: 'jamaica-black', label: 'Jamaica Black', bg: 'bg-jamaica-black', preview: 'from-jamaica-black to-jamaica-black/80' }
                ].map((theme) => (
                  <motion.div
                    key={theme.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setFormData({ ...formData, backgroundColor: theme.value as any })}
                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      formData.backgroundColor === theme.value
                        ? 'border-jamaica-green shadow-lg shadow-jamaica-green/20'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`h-8 w-full rounded-lg bg-gradient-to-r ${theme.preview} mb-3`}></div>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 ${theme.bg} rounded-full`}></div>
                      <span className="font-medium text-gray-900">{theme.label}</span>
                    </div>
                    {formData.backgroundColor === theme.value && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-6 h-6 bg-jamaica-green rounded-full flex items-center justify-center"
                      >
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Optional Link */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="space-y-3"
            >
              <Label htmlFor="link" className="text-base font-semibold text-gray-900">Optional Link (URL or path)</Label>
              <Input
                id="link"
                placeholder="https://example.com or /products/on-sale"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="h-11 rounded-xl border-gray-200 text-base"
              />
              <p className="text-xs text-gray-500">If set, clicking the banner navigates to this link.</p>
            </motion.div>

            {/* Banner Position */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-3"
            >
              <Label className="text-base font-semibold text-gray-900">Position on Website</Label>
              <Select
                value={formData.order.toString()}
                onValueChange={(value) => setFormData({ ...formData, order: parseInt(value) })}
              >
                <SelectTrigger className="h-12 rounded-xl border-gray-200 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">🔝 Banner 1 (Top Priority)</SelectItem>
                  <SelectItem value="2">🔻 Banner 2 (Secondary)</SelectItem>
                </SelectContent>
              </Select>
            </motion.div>

            {/* Advanced Options */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-6"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-jamaica-green" />
                Advanced Options
              </Label>
              
              <div className="space-y-6 bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                {/* Active Status */}
                <div className="flex items-center justify-between group">
                  <div className="space-y-1">
                    <Label className="text-base font-medium text-gray-900">Live Status</Label>
                    <p className="text-sm text-gray-500">Make banner visible on your website</p>
                  </div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                      className="data-[state=checked]:bg-jamaica-green"
                    />
                  </motion.div>
                </div>

                {/* Show Close Button */}
                <div className="flex items-center justify-between group">
                  <div className="space-y-1">
                    <Label className="text-base font-medium text-gray-900 flex items-center gap-2">
                      <MousePointer className="h-4 w-4 text-gray-400" />
                      Allow Dismissal
                    </Label>
                    <p className="text-sm text-gray-500">Let users close the banner if they want</p>
                  </div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch
                      checked={formData.showCloseButton}
                      onCheckedChange={(checked) => setFormData({ ...formData, showCloseButton: checked })}
                      className="data-[state=checked]:bg-jamaica-green"
                    />
                  </motion.div>
                </div>

                {/* Countdown Timer */}
                <div className="flex items-center justify-between group">
                  <div className="space-y-1">
                    <Label className="text-base font-medium text-gray-900 flex items-center gap-2">
                      <Timer className="h-4 w-4 text-gray-400" />
                      Countdown Timer
                    </Label>
                    <p className="text-sm text-gray-500">Create urgency with a live countdown</p>
                  </div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch
                      checked={formData.countdown.enabled}
                      onCheckedChange={(checked) => 
                        setFormData({ 
                          ...formData, 
                          countdown: { ...formData.countdown, enabled: checked }
                        })
                      }
                      className="data-[state=checked]:bg-jamaica-green"
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Banner Limit Info */}
            {/* {formData.isActive && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 }}
                className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl"
              >
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Banner Limit Policy</p>
                  <p>Only 2 banners can be active at the same time. After that, any updates made will overwrite existing banners.</p>
                </div>
              </motion.div>
            )} */}

            {/* Countdown Settings */}
            <AnimatePresence>
              {formData.countdown.enabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0, scale: 0.95 }}
                  animate={{ opacity: 1, height: 'auto', scale: 1 }}
                  exit={{ opacity: 0, height: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 bg-gradient-to-br from-jamaica-green/5 to-transparent border-l-4 border-jamaica-green rounded-r-xl p-6"
                >
                  <Label className="text-base font-semibold text-jamaica-green flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Countdown Configuration
                  </Label>
                  
                  {/* End Date */}
                  <div className="space-y-3">
                    <Label htmlFor="countdownEndDate" className="text-sm font-medium text-gray-900">
                      End Date & Time *
                    </Label>
                    <Input
                      id="countdownEndDate"
                      type="datetime-local"
                      value={formData.countdown.endDate}
                      onChange={(e) => 
                        setFormData({ 
                          ...formData, 
                          countdown: { ...formData.countdown, endDate: e.target.value }
                        })
                      }
                      className={`h-12 rounded-xl border-gray-200 text-base ${
                        errors.countdownEndDate ? 'border-red-300 focus:border-red-500' : 'focus:border-jamaica-green'
                      }`}
                    />
                    {errors.countdownEndDate && (
                      <motion.p 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-sm text-red-600"
                      >
                        {errors.countdownEndDate}
                      </motion.p>
                    )}
                  </div>

                  {/* Format & Text */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-900">Display Format</Label>
                      <Select
                        value={formData.countdown.format}
                        onValueChange={(value: 'days' | 'hours' | 'minutes' | 'full') => 
                          setFormData({ 
                            ...formData, 
                            countdown: { ...formData.countdown, format: value }
                          })
                        }
                      >
                        <SelectTrigger className="h-10 rounded-lg text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">⏰ Full Timer</SelectItem>
                          <SelectItem value="days">📅 Days Only</SelectItem>
                          <SelectItem value="hours">🕐 Hours Only</SelectItem>
                          <SelectItem value="minutes">⏱️ Minutes Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="countdownPrefix" className="text-sm font-medium text-gray-900">
                        Prefix Text
                      </Label>
                      <Input
                        id="countdownPrefix"
                        placeholder="Ends in:"
                        value={formData.countdown.prefix}
                        onChange={(e) => 
                          setFormData({ 
                            ...formData, 
                            countdown: { ...formData.countdown, prefix: e.target.value }
                          })
                        }
                        className="h-10 rounded-lg text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="countdownSuffix" className="text-sm font-medium text-gray-900">
                        Suffix Text
                      </Label>
                      <Input
                        id="countdownSuffix"
                        placeholder="remaining"
                        value={formData.countdown.suffix}
                        onChange={(e) => 
                          setFormData({ 
                            ...formData, 
                            countdown: { ...formData.countdown, suffix: e.target.value }
                          })
                        }
                        className="h-10 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live Preview */}
            <AnimatePresence>
              {formData.text && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: 0.7 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Eye className="h-4 w-4 text-jamaica-green" />
                    Live Preview
                  </Label>
                  <div className="relative overflow-hidden rounded-xl">
                    <div className={`p-6 text-white text-center relative ${
                      formData.backgroundColor === 'jamaica-green' 
                        ? 'bg-gradient-to-r from-jamaica-green to-jamaica-green/90' 
                        : 'bg-gradient-to-r from-jamaica-black to-jamaica-black/90'
                    }`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
                      <p className="font-semibold text-lg relative z-10">{formData.text}</p>
                      {formData.countdown.enabled && formData.countdown.endDate && (
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-sm mt-2 opacity-90 relative z-10"
                        >
                          {formData.countdown.prefix} [Live Countdown] {formData.countdown.suffix}
                        </motion.p>
                      )}
                      <div className="absolute top-0 left-0 w-full h-1 bg-white/20"></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  onClick={handleClose} 
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.text.trim()}
                  className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Updating Banner...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Banner
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
} 
